import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, Trash2, LogOut, ImageIcon, GripVertical, Pencil, X, Check } from "lucide-react";
import logo from "@/assets/logo.png";

const CATEGORIES = [
  { value: "infissi-finestre-alluminio", label: "Infissi — Finestre in alluminio" },
  { value: "infissi-porte-scorrevoli", label: "Infissi — Porte scorrevoli" },
  { value: "infissi-persiane-avvolgibili", label: "Infissi — Persiane e avvolgibili" },
  { value: "infissi-porte-blindate", label: "Infissi — Porte blindate" },
  { value: "infissi-zanzariere", label: "Infissi — Zanzariere" },
  { value: "infissi-finestre-legno", label: "Infissi — Finestre in legno" },
  { value: "outdoor-pergole", label: "Outdoor — Pergole bioclimatiche" },
  { value: "outdoor-tende-sole", label: "Outdoor — Tende da sole" },
  { value: "outdoor-gazebi", label: "Outdoor — Gazebi" },
  { value: "outdoor-tettoie", label: "Outdoor — Tettoie" },
  { value: "outdoor-arredi", label: "Outdoor — Arredi esterni" },
  { value: "outdoor-chiusure", label: "Outdoor — Chiusure verticali" },
  { value: "ferro-cancelli", label: "Ferro — Cancelli" },
  { value: "ferro-ringhiere", label: "Ferro — Ringhiere e balconi" },
  { value: "ferro-grate", label: "Ferro — Grate di sicurezza" },
  { value: "ferro-scale", label: "Ferro — Scale" },
  { value: "ferro-recinzioni", label: "Ferro — Recinzioni" },
  { value: "ferro-balconate", label: "Ferro — Balconate" },
];

const COLORS = [
  { value: "bianco", label: "Bianco", hex: "#FFFFFF" },
  { value: "nero", label: "Nero", hex: "#1a1a1a" },
  { value: "grigio", label: "Grigio", hex: "#808080" },
  { value: "marrone", label: "Marrone", hex: "#8B4513" },
  { value: "beige", label: "Beige", hex: "#D2B48C" },
  { value: "verde", label: "Verde", hex: "#2E8B57" },
  { value: "blu", label: "Blu", hex: "#2563EB" },
  { value: "rosso", label: "Rosso", hex: "#DC2626" },
  { value: "antracite", label: "Antracite", hex: "#383838" },
  { value: "avorio", label: "Avorio", hex: "#FFFFF0" },
  { value: "bronzo", label: "Bronzo", hex: "#CD7F32" },
  { value: "corten", label: "Corten", hex: "#8B4000" },
  { value: "legno-naturale", label: "Effetto legno", hex: "#DEB887" },
  { value: "oro", label: "Oro", hex: "#DAA520" },
  { value: "argento", label: "Argento", hex: "#C0C0C0" },
];

interface PortfolioProject {
  id: string;
  title: string;
  description: string | null;
  category: string;
  color: string | null;
  image_url: string;
  sort_order: number;
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<PortfolioProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [filterCategory, setFilterCategory] = useState("");

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [color, setColor] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const fetchProjects = useCallback(async () => {
    const { data } = await supabase
      .from("portfolio_projects")
      .select("*")
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: false });
    setProjects((data as PortfolioProject[]) || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    // Check auth
    supabase.auth.onAuthStateChange((event, session) => {
      if (!session) navigate("/admin/login");
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) navigate("/admin/login");
    });
    fetchProjects();
  }, [navigate, fetchProjects]);

  const compressImage = (file: File, maxWidth = 1200, quality = 0.8): Promise<Blob> => {
    return new Promise((resolve) => {
      const img = new Image();
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d")!;

      img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => resolve(blob!),
          "image/webp",
          quality
        );
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = (file: File) => {
    if (!file.type.startsWith("image/")) return;
    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile || !title || !category) return;

    setUploading(true);

    try {
      // Compress
      const compressed = await compressImage(selectedFile);
      const fileName = `${Date.now()}-${selectedFile.name.replace(/\.[^/.]+$/, "")}.webp`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from("portfolio")
        .upload(fileName, compressed, { contentType: "image/webp" });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from("portfolio")
        .getPublicUrl(fileName);

      // Insert into DB
      const { error: dbError } = await supabase
        .from("portfolio_projects")
        .insert({
          title,
          description: description || null,
          category,
          color: color || null,
          image_url: urlData.publicUrl,
          sort_order: projects.length,
        });

      if (dbError) throw dbError;

      // Reset form
      setTitle("");
      setDescription("");
      setCategory("");
      setColor("");
      setSelectedFile(null);
      setPreview(null);
      fetchProjects();
    } catch (err) {
      console.error(err);
      alert("Errore durante il caricamento");
    }

    setUploading(false);
  };

  const handleDelete = async (project: PortfolioProject) => {
    if (!confirm(`Eliminare "${project.title}"?`)) return;

    // Extract filename from URL
    const url = new URL(project.image_url);
    const pathParts = url.pathname.split("/portfolio/");
    if (pathParts[1]) {
      await supabase.storage.from("portfolio").remove([pathParts[1]]);
    }

    await supabase.from("portfolio_projects").delete().eq("id", project.id);
    fetchProjects();
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/admin/login");
  };

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editColor, setEditColor] = useState("");

  const startEditing = (project: PortfolioProject) => {
    setEditingId(project.id);
    setEditTitle(project.title);
    setEditDescription(project.description || "");
    setEditCategory(project.category);
    setEditColor(project.color || "");
  };

  const cancelEditing = () => setEditingId(null);

  const handleUpdate = async (id: string) => {
    if (!editTitle || !editCategory) return;
    await supabase
      .from("portfolio_projects")
      .update({ title: editTitle, description: editDescription || null, category: editCategory, color: editColor || null })
      .eq("id", id);
    setEditingId(null);
    fetchProjects();
  };

  const filteredProjects = filterCategory
    ? projects.filter((p) => p.category === filterCategory)
    : projects;

  const inputClass =
    "w-full bg-transparent border border-foreground/20 rounded-sm px-4 py-3 text-foreground font-body text-sm placeholder:text-foreground/40 focus:outline-none focus:border-accent transition-colors duration-300";

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 md:px-12 py-4 backdrop-blur-md bg-background/70 border-b border-border">
        <div className="flex items-center gap-4">
          <img src={logo} alt="Logo" className="h-8" />
          <span className="font-heading text-sm uppercase tracking-wider text-foreground/70">
            Admin Panel
          </span>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 text-foreground/50 hover:text-foreground text-sm font-body transition-colors"
        >
          <LogOut size={16} />
          Esci
        </button>
      </div>

      <div className="pt-24 pb-16 px-6 md:px-12 max-w-7xl mx-auto">
        {/* Upload Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16"
        >
          <h2 className="font-heading text-2xl font-bold text-foreground uppercase tracking-wider mb-8">
            Carica nuovo lavoro
          </h2>

          <form onSubmit={handleUpload} className="space-y-5">
            {/* Drag & Drop Area */}
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => document.getElementById("file-input")?.click()}
              className={`border-2 border-dashed rounded-sm p-8 md:p-12 text-center cursor-pointer transition-all duration-300 ${
                dragOver
                  ? "border-accent bg-accent/10"
                  : preview
                  ? "border-accent/50"
                  : "border-foreground/20 hover:border-foreground/40"
              }`}
            >
              {preview ? (
                <div className="flex flex-col items-center gap-4">
                  <img
                    src={preview}
                    alt="Preview"
                    className="max-h-48 rounded-sm object-contain"
                  />
                  <p className="text-sm text-foreground/50 font-body">
                    {selectedFile?.name} — Clicca per cambiare
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <Upload size={32} className="text-foreground/30" />
                  <p className="text-foreground/50 font-body text-sm">
                    Trascina una foto qui o clicca per selezionarla
                  </p>
                  <p className="text-foreground/30 font-body text-xs">
                    L'immagine verrà compressa automaticamente in WebP
                  </p>
                </div>
              )}
              <input
                id="file-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelect(file);
                }}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <input
                type="text"
                placeholder="Titolo *"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={inputClass}
                required
              />
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className={`${inputClass} ${!category ? "text-foreground/40" : ""} bg-background`}
                required
              >
                <option value="" disabled>
                  Categoria *
                </option>
                {CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Color selector */}
            <div>
              <p className="text-foreground/50 font-body text-xs mb-2 uppercase tracking-wider">Colore (opzionale)</p>
              <div className="flex flex-wrap gap-2">
                {COLORS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setColor(color === c.value ? "" : c.value)}
                    className={`w-8 h-8 rounded-full border-2 transition-all duration-200 ${
                      color === c.value ? "border-accent scale-110 ring-2 ring-accent/30" : "border-foreground/20 hover:border-foreground/40"
                    }`}
                    style={{ backgroundColor: c.hex }}
                    title={c.label}
                  />
                ))}
              </div>
              {color && (
                <p className="text-foreground/40 font-body text-xs mt-1">
                  Selezionato: {COLORS.find(c => c.value === color)?.label}
                </p>
              )}
            </div>

            <textarea
              placeholder="Descrizione (opzionale)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className={`${inputClass} resize-none`}
            />

            <button
              type="submit"
              disabled={uploading || !selectedFile || !title || !category}
              className="cta-button hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-300 disabled:opacity-30 disabled:pointer-events-none"
            >
              {uploading ? "Caricamento..." : "Pubblica lavoro"}
            </button>
          </form>
        </motion.div>

        {/* Projects List */}
        <div>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <h2 className="font-heading text-2xl font-bold text-foreground uppercase tracking-wider">
              Lavori pubblicati ({filteredProjects.length})
            </h2>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className={`${inputClass} max-w-xs bg-background`}
            >
              <option value="">Tutte le categorie</option>
              {CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          {loading ? (
            <p className="text-foreground/40 font-body">Caricamento...</p>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-16">
              <ImageIcon size={48} className="mx-auto text-foreground/20 mb-4" />
              <p className="text-foreground/40 font-body">Nessun lavoro caricato</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <AnimatePresence>
                {filteredProjects.map((project) => (
                  <motion.div
                    key={project.id}
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="group relative"
                  >
                    <div className="aspect-square overflow-hidden rounded-sm">
                      <img
                        src={project.image_url}
                        alt={project.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {editingId === project.id ? (
                      <div className="mt-3 space-y-2">
                        <input
                          type="text"
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          className={`${inputClass} !py-2 text-xs`}
                          placeholder="Titolo"
                        />
                        <select
                          value={editCategory}
                          onChange={(e) => setEditCategory(e.target.value)}
                          className={`${inputClass} !py-2 text-xs bg-background`}
                        >
                          {CATEGORIES.map((cat) => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                          ))}
                        </select>
                        <div>
                          <p className="text-foreground/50 font-body text-[10px] mb-1 uppercase tracking-wider">Colore</p>
                          <div className="flex flex-wrap gap-1.5">
                            {COLORS.map((c) => (
                              <button
                                key={c.value}
                                type="button"
                                onClick={() => setEditColor(editColor === c.value ? "" : c.value)}
                                className={`w-6 h-6 rounded-full border-2 transition-all duration-200 ${
                                  editColor === c.value ? "border-accent scale-110 ring-2 ring-accent/30" : "border-foreground/20 hover:border-foreground/40"
                                }`}
                                style={{ backgroundColor: c.hex }}
                                title={c.label}
                              />
                            ))}
                          </div>
                        </div>
                        <textarea
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          className={`${inputClass} !py-2 text-xs resize-none`}
                          rows={2}
                          placeholder="Descrizione"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleUpdate(project.id)}
                            className="flex items-center gap-1 text-xs text-green-400 hover:text-green-300 transition-colors"
                          >
                            <Check size={14} /> Salva
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="flex items-center gap-1 text-xs text-foreground/40 hover:text-foreground transition-colors"
                          >
                            <X size={14} /> Annulla
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading text-sm font-semibold text-foreground uppercase tracking-wide truncate">
                            {project.title}
                          </h3>
                          {project.description && (
                            <p className="text-foreground/40 font-body text-xs mt-0.5 truncate">
                              {project.description}
                            </p>
                          )}
                          <div className="flex items-center gap-2">
                            <p className="text-accent/70 font-body text-xs">
                              {CATEGORIES.find((c) => c.value === project.category)?.label || project.category}
                            </p>
                            {project.color && (
                              <span
                                className="inline-block w-3 h-3 rounded-full border border-foreground/20"
                                style={{ backgroundColor: COLORS.find(c => c.value === project.color)?.hex || '#888' }}
                                title={COLORS.find(c => c.value === project.color)?.label || project.color}
                              />
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <button
                            onClick={() => startEditing(project)}
                            className="text-foreground/20 hover:text-accent transition-colors p-1"
                            title="Modifica"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(project)}
                            className="text-foreground/20 hover:text-red-400 transition-colors p-1"
                            title="Elimina"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
